import { Suspense, useEffect, useState, useRef } from "react";
import { Outlet } from "react-router-dom";
import { Box, CssBaseline, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import BottomNavigationList from "src/components/layout/mainLayout/bottomNavigation";
import useNetwork from "src/hooks/useNetwork";
import Main from "src/components/layout/mainLayout/main/Main";
import AppBar from "src/components/layout/mainLayout/appBar";
import NewChatListener from "src/components/layout/mainLayout/NewChatListener";
import StripeInfoUpdate from "src/components/layout/mainLayout/StripeInfoUpdate";
import Sidebar from "src/components/layout/mainLayout/navigationList";
import UserIncompleteInfoModal from "src/components/layout/mainLayout/userIncompleteInfoModal";
import CallControl from "src/components/pages/chat/ChatContainer/InboxAndMessageBox/MessageBox/CallControl";

import useStyles from "./Styles";

interface ModalRefType {
  handleOpen: () => void;
  handleClose: () => void;
}

const Layout = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [isVerifying, setIsVerifying] = useState(false);

  const { drawer } = useSelector((state: any) => state.drawer);
  const me = useSelector((state: any) => state.me.me); // 👈 grab user info

  const [modalType, setModalType] = useState("");
  const changeModalType = (type: string) => setModalType(type);
  const userInfoRef = useRef<ModalRefType | null>(null);

  const handleUserIncompleteInfoModal = (mode: "open" | "close") => {
    const ref = userInfoRef.current;
    if (mode === "open") ref?.handleOpen();
    else if (mode === "close") ref?.handleClose();
  };

  const isOnline = useNetwork();
  const [wasOffline, setWasOffline] = useState(false);

  // 🔥 show modal if email missing
  useEffect(() => {
    if (!me || isVerifying) return;
    if (me && !me.email) {
      handleUserIncompleteInfoModal("open");
      changeModalType("email");
    } else if (
      me &&
      me.email &&
      (me.facebookId || me.instagramId) &&
      !me.verified
    ) {
      handleUserIncompleteInfoModal("open");
      changeModalType("verify");
    }
  }, [me, isVerifying]);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      enqueueSnackbar("Internet connection lost.", { variant: "error" });
    } else if (wasOffline) {
      setWasOffline(false);
      enqueueSnackbar("Connection recovered.", { variant: "success" });
    }
  }, [isOnline]);

  return (
    <Box
      className={classes.box}
      sx={{ display: "flex", minHeight: "100vh", flexDirection: "column" }}
    >
      <AppBar />
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <CssBaseline />
        <NewChatListener />
        <StripeInfoUpdate />
        <Sidebar />
        <CallControl />

        <Box
          className={classes.rightContainer}
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Main Content */}
          <Main theme={theme}>
            <Box
              className={classes.mainBox}
              sx={{
                marginLeft: drawer ? "240px" : "85px",
                [theme.breakpoints.down("md")]: {
                  marginLeft: 0,
                },
                transition: "margin-left 0.3s ease-in-out",
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      minHeight: "60vh",
                    }}
                  >
                    <CircularProgress color="primary" />
                  </Box>
                }
              >
                <Outlet />
              </Suspense>
            </Box>
          </Main>

          <BottomNavigationList />
        </Box>
      </Box>

      {/* 👇 Place modal at root so it can overlay everything */}
      <UserIncompleteInfoModal
        setIsVerifying={setIsVerifying}
        modalRef={userInfoRef}
        modalType={modalType}
        userEmail={me && me.email}
        changeModalType={changeModalType}
        onClose={() => handleUserIncompleteInfoModal("close")}
      />
    </Box>
  );
};

export default Layout;
